'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import type { ImageElementConfig } from '@/lib/pageBuilder';

type ThemeOption = {
  slug: string;
  name: string;
  logo?: ImageElementConfig;
};

type Props = {
  themes: ThemeOption[];
  defaultTheme: string;
  siteTitle: string;
};

function getActiveThemeSlug(fallback: string) {
  if (typeof document === 'undefined') return fallback;
  return document.documentElement.getAttribute('data-theme') || fallback;
}

export default function ThemeBrand({ themes, defaultTheme, siteTitle }: Props) {
  const [themeSlug, setThemeSlug] = useState(defaultTheme);

  useEffect(() => {
    setThemeSlug(getActiveThemeSlug(defaultTheme));
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setThemeSlug(getActiveThemeSlug(defaultTheme));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [defaultTheme]);

  const activeTheme = useMemo(() => themes.find(theme => theme.slug === themeSlug) || themes[0], [themes, themeSlug]);
  const logoUrl = activeTheme?.logo?.url?.trim();
  const logoAlt = (activeTheme?.logo?.alt || '').trim() || `${siteTitle} logo`;
  const configuredWidth = Number(activeTheme?.logo?.intrinsicWidth) || 0;
  const configuredHeight = Number(activeTheme?.logo?.intrinsicHeight) || 0;
  const [logoSize, setLogoSize] = useState({ width: configuredWidth, height: configuredHeight });

  useEffect(() => {
    setLogoSize({ width: configuredWidth, height: configuredHeight });
  }, [configuredHeight, configuredWidth, logoUrl]);

  return (
    <Link href="/" className="site-brand-link" aria-label={`Go to ${siteTitle} home`}>
      {logoUrl ? (
        <img
          className="site-brand-logo"
          src={logoUrl}
          alt={logoAlt}
          width={logoSize.width || undefined}
          height={logoSize.height || undefined}
          onLoad={event => {
            const image = event.currentTarget;
            if (image.naturalWidth && image.naturalHeight) {
              setLogoSize({ width: image.naturalWidth, height: image.naturalHeight });
            }
          }}
        />
      ) : null}
      <span className="site-brand-text">{siteTitle}</span>
    </Link>
  );
}
